import { doc, setDoc } from 'firebase/firestore';
import type { BankAccount, BankAccountKind, CurrencyCode } from '../types';
import { newId } from '../utils/id';
import { normalizeWebsiteUrl } from '../utils/favicon';
import { db } from './firebase';
import { COLLECTION_BANK_ACCOUNTS, ownedItemDocId } from './firestorePaths';

export type ManualBankAccountInput = {
  id?: string;
  name: string;
  websiteUrl?: string;
  balance: number;
  ibanMasked?: string;
  accountNumber?: string;
  kind?: BankAccountKind;
  currency?: CurrencyCode;
  /** When updating, pass fields from app state — Firestore denies get on non-existent owned docs. */
  existing?: Pick<BankAccount, 'createdAt' | 'provider'>;
};

function stripUndefinedFields(obj: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) next[key] = value;
  }
  return next;
}

export async function saveManualBankAccount(
  userId: string,
  input: ManualBankAccountInput
): Promise<BankAccount> {
  const name = input.name.trim();
  if (!name) {
    throw new Error('Account name is required.');
  }

  const balance = Number(input.balance);
  if (!Number.isFinite(balance)) {
    throw new Error('Enter a valid balance.');
  }

  const now = new Date().toISOString();
  const id = input.id?.trim() || newId();

  if (input.existing && input.existing.provider !== 'manual') {
    throw new Error('This account is linked to a bank connection and cannot be edited manually.');
  }

  const docRef = doc(db, COLLECTION_BANK_ACCOUNTS, ownedItemDocId(userId, id));

  const websiteUrl = normalizeWebsiteUrl(input.websiteUrl);
  const ibanMasked = input.ibanMasked?.trim() ?? '';
  const accountNumber = input.accountNumber?.trim() || undefined;

  const account: BankAccount = {
    id,
    userId,
    connectionId: '',
    provider: 'manual',
    externalId: id,
    name,
    websiteUrl,
    ibanMasked,
    accountNumber,
    kind: input.kind ?? 'account',
    currency: input.currency ?? 'EUR',
    balance,
    balanceUpdatedAt: now,
    createdAt: input.existing?.createdAt ?? now,
    updatedAt: now,
  };

  await setDoc(
    docRef,
    stripUndefinedFields({
      ...account,
    }),
    { merge: true }
  );

  return account;
}
